import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Users } from "lucide-react";
import ZButton from "@/components/ZButton";
import { ZInput, ZTextarea } from "@/components/ZInput";
import Modal from "@/components/Modal";
import TermsConsent from "@/components/TermsConsent";
import ShareCelebrationModal from "@/components/ShareCelebrationModal";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function FundraiserDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [amount, setAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [celebration, setCelebration] = useState(null); // { amount } | null

  const load = () => {
    api.get(`/fundraisers/${id}`).then((r) => setF(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const submit = async () => {
    setErr("");
    const a = parseFloat(amount);
    if (!a || a <= 0) return setErr("Montant invalide.");
    if (!acceptTerms) return setErr("Vous devez accepter les CGV et la politique de confidentialité.");
    setSending(true);
    try {
      await api.post(`/fundraisers/${id}/contribute`, {
        amount: a,
        donor_name: anonymous ? null : (donorName.trim() || null),
        donor_email: donorEmail.trim() || null,
        message: message.trim() || null,
        anonymous,
      });
      setSuccess(true);
      setShow(false);
      setCelebration({ amount: a, anonymous });
      setAmount(""); setDonorName(""); setDonorEmail(""); setMessage(""); setAnonymous(false); setAcceptTerms(false);
      load();
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!f) return <div className="z-container">Cagnotte introuvable.</div>;

  const pct = f.goal > 0 ? Math.min(100, Math.round((f.raised / f.goal) * 100)) : 0;

  return (
    <div data-testid="fundraiser-detail">
      {f.image_url && (
        <div style={{ width: "100%", aspectRatio: "16/9", maxHeight: 320, overflow: "hidden" }}>
          <img src={f.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <div className="z-container">
        <h1 className="z-page-title">{f.title}</h1>
        <p style={{ color: "var(--z-muted)", margin: "0 0 18px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>par {f.organizer_name}</span>
          <VerifiedBadge verified={f.organizer_verified} variant="pill" testId="fundraiser-verified" />
        </p>

        <div className="z-card" style={{ padding: 18, marginBottom: 18 }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--z-teal)" }} data-testid="fundraiser-raised">
            {f.raised.toFixed(0)} €
          </div>
          <div style={{ fontSize: 13, color: "var(--z-muted)" }}>récoltés sur {f.goal.toFixed(0)} €</div>
          <div className="z-progress" style={{ margin: "12px 0 6px" }}>
            <div className="z-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--z-muted)" }}>
            <Users size={14} /> {f.contributors_count} contributeur(s)
          </div>
        </div>

        <p style={{ lineHeight: 1.6, whiteSpace: "pre-wrap", color: "var(--z-text)", marginBottom: 20 }}>
          {f.description}
        </p>

        {success && (
          <div style={{ background: "rgba(67,122,34,0.10)", color: "var(--z-success)", padding: 12, borderRadius: 10, marginBottom: 14, fontWeight: 600 }} data-testid="fundraiser-success">
            Merci pour votre contribution !
          </div>
        )}

        <ZButton onClick={() => { setErr(""); setAcceptTerms(false); setShow(true); }} block data-testid="fundraiser-contribute-btn">
          <Heart size={16} /> Faire un don
        </ZButton>

        {f.recent_contributions?.length > 0 && (
          <div style={{ marginTop: 28 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Contributeurs récents</h3>
            <div style={{ display: "grid", gap: 8 }}>
              {f.recent_contributions.map((c) => (
                <div key={c.id} className="z-card" style={{ padding: 12, display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{c.donor_name}</div>
                    {c.message && <div style={{ fontSize: 13, color: "var(--z-muted)" }}>« {c.message} »</div>}
                  </div>
                  <div style={{ fontWeight: 700, color: "var(--z-teal)" }}>{c.amount.toFixed(0)} €</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Modal open={show} onClose={() => setShow(false)} title="Votre contribution" testId="contribute-modal">
        <ZInput
          type="number" label="Montant (€)" value={amount}
          onChange={(e) => setAmount(e.target.value)} min={1} step="0.01" required
          data-testid="contribute-amount"
        />
        {f.suggested_tiers?.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            {f.suggested_tiers.map((t) => (
              <button key={t} type="button" className="z-chip" onClick={() => setAmount(String(t))} data-testid={`contribute-tier-${t}`}>
                {t} €
              </button>
            ))}
          </div>
        )}
        {!anonymous && (
          <>
            <ZInput
              label={user ? "Nom (optionnel)" : "Votre nom"} value={donorName || user?.name || ""}
              onChange={(e) => setDonorName(e.target.value)} placeholder="Aïssa Bah"
              data-testid="contribute-name"
            />
            <ZInput
              type="email" label="E-mail (pour le reçu)" value={donorEmail || user?.email || ""}
              onChange={(e) => setDonorEmail(e.target.value)} placeholder="vous@exemple.com"
              data-testid="contribute-email"
            />
          </>
        )}
        <ZTextarea
          label="Message (optionnel)" value={message}
          onChange={(e) => setMessage(e.target.value)} placeholder="Bonne continuation !"
          data-testid="contribute-message"
        />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="contribute-anonymous" style={{ accentColor: "var(--z-teal)" }} />
          Don anonyme
        </label>
        {err && <p className="z-error-text" data-testid="contribute-error">{err}</p>}
        <TermsConsent checked={acceptTerms} onChange={setAcceptTerms} testId="contribute-terms-consent" />
        <ZButton onClick={submit} block loading={sending} disabled={!acceptTerms} data-testid="contribute-submit-btn">
          Contribuer (paiement simulé)
        </ZButton>
      </Modal>

      <ShareCelebrationModal
        open={!!celebration}
        onClose={() => setCelebration(null)}
        kind="donation"
        entityId={f.id || id}
        title={f.title}
        subtitle={`Cagnotte de ${f.organizer_name}`}
        amount={celebration?.amount}
        currency="€"
        imageUrl={f.image_url || null}
        guestEmail={!user ? (donorEmail || "").trim() : undefined}
        guestName={!user ? (donorName || "").trim() : undefined}
        primaryCtaLabel="Merci, continuer"
        onPrimaryCta={() => setCelebration(null)}
      />
    </div>
  );
}
